import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import FilterBuilderCollection from './FilterBuilderCollection'

/**
 * Top-level wrapper for the scope-filter editor (SMT-1608 Phase 1).
 *
 * Normalizes the in-memory tree into the canonical "root collection"
 * shape so the recursive renderer can assume the root is always a
 * filter-collection. A null tree (no filter) is presented as an empty
 * "All of" collection so the user has a place to add the first
 * predicate. When the editor produces an empty collection we surface
 * `null` back upward so the surrounding dialog drops the entry from
 * `scopeFilters`.
 */
const EMPTY_ROOT = { $type: 'filter-collection', method: 'All', filters: [] }

export default function FilterBuilder ({ tree, onChange, classes }) {
  const root = useMemo(() => {
    if (tree == null) return EMPTY_ROOT
    // A bare predicate or preset gets wrapped in an "All of" collection
    // so the recursive renderer can treat the root uniformly.
    if (tree.$type !== 'filter-collection') {
      return { $type: 'filter-collection', method: 'All', filters: [tree] }
    }
    return tree
  }, [tree])

  const handleRootChange = useCallback((next) => {
    if (!next || (next.$type === 'filter-collection' && (next.filters || []).length === 0)) {
      onChange(null)
    } else {
      onChange(next)
    }
  }, [onChange])

  return (
    <FilterBuilderCollection
      tree={root}
      onChange={handleRootChange}
      isRoot
      classes={classes}
    />
  )
}

FilterBuilder.propTypes = {
  tree: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired
}
